import { useState } from "react";
import { View } from "react-native";

import { useWindowDimensionsQuery } from "@smartrent/hooks";

import { DialogContent } from "./DialogContent";
import { DialogTitle } from "./DialogTitle";
import { DialogDescription } from "./DialogDescription";
import { useTheme } from "../../utils/theme";
import { DialogCheckbox } from "./DialogCheckbox";
import { DialogAction } from "./DialogAction";

import type { FC, ComponentType } from "react";
import type { DialogBaseProps } from "./DialogBase";
import type { DialogActionProps } from "./DialogAction";
import type { CheckedType } from "../CheckboxBase";

export interface ConfirmDialogBaseProps
  extends Pick<
    DialogBaseProps,
    "visible" | "onClose" | "testID" | "backdropOpacity"
  > {
  title: string;
  description?: string | React.ReactElement;
  titleLabel?: string;
  descriptionLabel?: string;
  confirmType?: DialogActionProps["type"];
  onConfirm: VoidFunction;
  confirmText?: string;
  cancelText?: string;
  confirmButtonDisabled?: boolean;
  confirmButtonLoading?: boolean;
  Dialog: FC<DialogBaseProps>;
  AutoFocusInside?: FC<any> | ComponentType<any>;
  onChangeChecked?: (checked: CheckedType) => void;
  checkboxLabel?: string;
}

// on web we want the confirm button to be autofocused for accessibility
// we don't want anything to happen on native
const PassThrough: FC<{ children: any }> = ({ children }) => {
  return <>{children}</>;
};

export function ConfirmDialogBase({
  title,
  backdropOpacity,
  description,
  titleLabel,
  descriptionLabel,
  visible,
  confirmType = "primary",
  confirmText = "Confirm",
  cancelText = "Cancel",
  onClose,
  onConfirm,
  testID,
  confirmButtonDisabled = false,
  confirmButtonLoading = false,
  Dialog,
  AutoFocusInside = PassThrough,
  onChangeChecked,
  checkboxLabel,
}: ConfirmDialogBaseProps) {
  const { spacing } = useTheme();
  const [checked, setChecked] = useState(false);
  const { small } = useWindowDimensionsQuery();

  return (
    <Dialog
      testID={testID}
      titleID={"title"}
      descriptionID={"desc"}
      visible={visible}
      backdropOpacity={backdropOpacity}
      onClose={() => {
        onClose();
        setChecked(false);
      }}
      closeOnOutsidePress={!confirmButtonDisabled}
    >
      <DialogContent>
        <View
          style={{
            paddingVertical: spacing.md,
            paddingHorizontal: spacing.xl,
            alignItems: "center",
            maxWidth: small ? 328 : 600,
          }}
        >
          <AutoFocusInside>
            <DialogTitle
              style={{ textAlign: "center" }}
              nativeID="title"
              accessibilityLabel={titleLabel}
            >
              {title}
            </DialogTitle>
          </AutoFocusInside>

          {description ? (
            <DialogDescription
              nativeID="desc"
              accessibilityLabel={descriptionLabel}
            >
              {description}
            </DialogDescription>
          ) : null}

          {/* this does nothing on native but on web it will autofocus the primary input per aria accessibility guidelines */}
          <View
            style={{
              alignItems: "center",
              flexDirection: small ? "column" : "row-reverse",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <DialogAction
              type={confirmType}
              onPress={() => {
                onConfirm();
                checked && onChangeChecked ? onChangeChecked(checked) : null;
              }}
              text={confirmText}
              style={{
                width: small ? 280 : 276,
              }}
              disabled={confirmButtonDisabled}
              isLoading={confirmButtonLoading}
            />
            <DialogAction
              type="cancel"
              onPress={() => {
                onClose();
                setChecked(false);
              }}
              text={cancelText}
              style={{
                marginTop: small ? spacing.lg : undefined,
                width: small ? 280 : 276,
              }}
              disabled={confirmButtonDisabled}
            />
          </View>
          {onChangeChecked ? (
            <DialogCheckbox
              checked={checked}
              onChangeChecked={setChecked}
              label={checkboxLabel}
              style={{ marginTop: 16 }}
            />
          ) : null}
        </View>
      </DialogContent>
    </Dialog>
  );
}
